import { type HipSpineLandmark, HipSpineLandmarkName } from '@/domain/anatomy/hipSpineLandmarks';
import { HipSpineAngleId } from '@/domain/anatomy/hipSpineAngleOverlays';
import { formusLightTheme } from '@/plugins/formusTheme';
import type { HipSpineAnnotationCategory } from './types';

export type HipSpineLandmarkGroup = {
    /** Unique key, X-ray overlay label, and line node name prefix (e.g. `line-${scene}`) */
    label: string;
    /** Full name shown in the sidebar subgroup list (e.g. "Lumbar Vertebra (L1)") */
    name?: string;
    landmarks: HipSpineLandmark[];
    /** Line node names to toggle; defaults to [`line-${scene}`] when not set */
    lineNames?: string[];
};

const colours = formusLightTheme.colors!;

export const HIP_SPINE_ANNOTATION_COLOURS: Record<HipSpineAnnotationCategory, string> = {
    lumbar: colours.cyan as string,
    sacrum: colours['point-pink'] as string,
    pelvis: colours.purple as string,
    femoral: colours.primary as string,
};

/**
 * Per-angle overlay colours, keyed by angle id. Unlike the region-grouped
 * HIP_SPINE_ANNOTATION_COLOURS (used by the Annotations tab), each angle has
 * its own colour so co-located angles in the same anatomical region — e.g.
 * Sacral Slope and Pelvic Incidence both draw off the sacrum — are visually
 * distinguishable. PFA1 and PFA2 share green as two instances of the same
 * pelvic-femoral angle. Values are the nearest existing formusTheme tokens.
 */
export const HIP_SPINE_ANGLE_COLOURS: Record<HipSpineAngleId, string> = {
    [HipSpineAngleId.LumbarLordosis]: colours.cyan as string,
    [HipSpineAngleId.SacralSlope]: colours.orange as string,
    [HipSpineAngleId.PelvicFemoral1]: colours.green as string,
    [HipSpineAngleId.PelvicFemoral2]: colours.green as string,
    [HipSpineAngleId.PelvicIncidence]: colours['point-pink'] as string,
    [HipSpineAngleId.AppTilt]: colours.yellow as string,
};

export type HipSpineAnnotationDef = {
    category: HipSpineAnnotationCategory;
    displayName: string;
    colour: string;
    subGroups: HipSpineLandmarkGroup[];
};

const L = HipSpineLandmarkName;

const LUMBAR_SUB_GROUPS: HipSpineLandmarkGroup[] = [
    {label: 'L1', name: 'Lumbar Vertebra (L1)', landmarks: [{name: L.L1Anterior}, {name: L.L1Posterior}]},
    {label: 'L2', name: 'Lumbar Vertebra (L2)', landmarks: [{name: L.L2Anterior}, {name: L.L2Posterior}]},
    {label: 'L3', name: 'Lumbar Vertebra (L3)', landmarks: [{name: L.L3Anterior}, {name: L.L3Posterior}]},
    {label: 'L4', name: 'Lumbar Vertebra (L4)', landmarks: [{name: L.L4Anterior}, {name: L.L4Posterior}]},
    {label: 'L5', name: 'Lumbar Vertebra (L5)', landmarks: [{name: L.L5Anterior}, {name: L.L5Posterior}]},
];

const SACRUM_SUB_GROUPS: HipSpineLandmarkGroup[] = [
    {
        label: 'SE',
        name: 'Sacral Endplate (SE)',
        landmarks: [{name: L.SacrumAnterior}, {name: L.SacrumPosterior}],
    },
];

const PELVIS_SUB_GROUPS: HipSpineLandmarkGroup[] = [
    {
        label: 'APP',
        name: 'Anterior Pelvic Plane (APP)',
        landmarks: [{name: L.ASIS1}, {name: L.ASIS2}, {name: L.PubicSymphysis}, {name: L.ASIS1}],
    }
]

const FEMUR_SUB_GROUPS: HipSpineLandmarkGroup[] = [
    {
        label: 'PF1',
        name: 'Proximal Femur 1 (PF1)',
        landmarks: [{name: L.HJC1}, {name: L.FemurCortex1}],
    },
    {
        label: 'PF2',
        name: 'Proximal Femur 2 (PF2)',
        landmarks: [{name: L.HJC2}, {name: L.FemurCortex2}],
    },
]

export const HIP_SPINE_POSTURE_ANNOTATIONS: Record<string, HipSpineAnnotationDef[]> = {
    'standing-sagittal': [
        {
            category: 'lumbar',
            displayName: 'Lumbar',
            colour: HIP_SPINE_ANNOTATION_COLOURS.lumbar,
            subGroups: LUMBAR_SUB_GROUPS,
        },
        {
            category: 'sacrum',
            displayName: 'Sacrum',
            colour: HIP_SPINE_ANNOTATION_COLOURS.sacrum,
            subGroups: SACRUM_SUB_GROUPS,
        },
        {
            category: 'pelvis',
            displayName: 'Pelvis',
            colour: HIP_SPINE_ANNOTATION_COLOURS.pelvis,
            subGroups: PELVIS_SUB_GROUPS,
        },
        {
            category: 'femoral',
            displayName: 'Femoral',
            colour: HIP_SPINE_ANNOTATION_COLOURS.femoral,
            subGroups: FEMUR_SUB_GROUPS,
        },
    ],
    'flexed-seated-sagittal': [
        {
            category: 'lumbar',
            displayName: 'Lumbar',
            colour: HIP_SPINE_ANNOTATION_COLOURS.lumbar,
            subGroups: LUMBAR_SUB_GROUPS,
        },
        {
            category: 'sacrum',
            displayName: 'Sacrum',
            colour: HIP_SPINE_ANNOTATION_COLOURS.sacrum,
            subGroups: SACRUM_SUB_GROUPS,
        },
    ],
    'lumbar-sagittal': [
        {
            category: 'lumbar',
            displayName: 'Lumbar',
            colour: HIP_SPINE_ANNOTATION_COLOURS.lumbar,
            subGroups: LUMBAR_SUB_GROUPS,
        },
        {
            category: 'sacrum',
            displayName: 'Sacrum',
            colour: HIP_SPINE_ANNOTATION_COLOURS.sacrum,
            subGroups: SACRUM_SUB_GROUPS,
        },
    ],
};

/** Set of landmark names to keep for each posture (derived from annotation definitions) */
export const HIP_SPINE_POSTURE_LANDMARK_NAMES: Record<string, Set<string>> = Object.fromEntries(
    Object.entries(HIP_SPINE_POSTURE_ANNOTATIONS).map(([posture, annotations]) => [
        posture,
        new Set(
            annotations.flatMap((a) =>
                a.subGroups.flatMap((g) => g.landmarks.map((lm) => lm.name)),
            ),
        ),
    ]),
);